import React, { useState } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Chip,
    Stack,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SportsFootballIcon from '@mui/icons-material/SportsFootball';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

interface FriendlyMatchEvent {
    id: number;
    name: string;
    date: string;
    time: string;
    location: string;
    capacity: number;
    participants: number;
    status: 'open' | 'full' | 'closed';
}

const FriendlyMatch: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark = theme.palette.mode === 'dark';

    const [events, setEvents] = useState<FriendlyMatchEvent[]>([
        {
            id: 1,
            name: 'Friendly Match - Pemula vs Menengah',
            date: '15 Juni 2025',
            time: '18:00 - 20:00',
            location: 'GOR Kebayoran, Jakarta Selatan',
            capacity: 20,
            participants: 18,
            status: 'open'
        },
        {
            id: 2,
            name: 'Friendly Match - Campuran Open',
            date: '22 Juni 2025',
            time: '19:00 - 21:00',
            location: 'Lapangan Badminton Cengkareng',
            capacity: 16,
            participants: 16,
            status: 'full'
        },
        {
            id: 3,
            name: 'Friendly Match - Senior Open',
            date: '29 Juni 2025',
            time: '17:00 - 19:00',
            location: 'GOR Gelora Bung Karno',
            capacity: 24,
            participants: 12,
            status: 'open'
        }
    ]);

    const [registrationOpen, setRegistrationOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<FriendlyMatchEvent | null>(null);
    const [successMessage, setSuccessMessage] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        level: '',
        experience: ''
    });

    const handleRegister = (event: FriendlyMatchEvent) => {
        if (event.status !== 'full') {
            setSelectedEvent(event);
            setRegistrationOpen(true);
        }
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmitRegistration = () => {
        if (formData.name && formData.phone && formData.level && selectedEvent) {
            // Simulate registration
            setEvents(events.map(e =>
                e.id === selectedEvent.id
                    ? { ...e, participants: e.participants + 1, status: e.participants + 1 === e.capacity ? 'full' : 'open' }
                    : e
            ));
            setRegistrationOpen(false);
            setSuccessMessage(true);
            setFormData({ name: '', phone: '', level: '', experience: '' });
            setTimeout(() => setSuccessMessage(false), 5000);
        }
    };

    const getStatusColor = (status: string): 'success' | 'warning' | 'error' => {
        switch (status) {
            case 'open':
                return 'success';
            case 'full':
                return 'error';
            case 'closed':
                return 'error';
            default:
                return 'success';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'open':
                return 'Terbuka';
            case 'full':
                return 'Penuh';
            case 'closed':
                return 'Ditutup';
            default:
                return 'Terbuka';
        }
    };

    return (
        <>
            <Helmet>
                <title>Friendly Match - Booking Mabar RaketerS</title>
                <meta name="description" content="Bergabunglah dengan event Friendly Match di komunitas RaketerS" />
            </Helmet>

            {/* Success Alert */}
            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    ✓ Pendaftaran berhasil! Tim kami akan menghubungi Anda segera.
                </Alert>
            )}

            {/* Hero Section */}
            <Box
                sx={{
                    background: isDark
                        ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 50%, #1565c0 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                    color: '#fff',
                    py: isMobile ? 4 : 8,
                    textAlign: 'center',
                    borderRadius: 3,
                    mb: 6,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        pointerEvents: 'none',
                    }
                }}
            >
                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Box
                            sx={{
                                background: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '50%',
                                p: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <SportsFootballIcon sx={{ fontSize: 60 }} />
                        </Box>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, textShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                        Friendly Match
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 3, opacity: 0.95, maxWidth: 600, mx: 'auto' }}>
                        Ajakan bermain badminton santai dan menyenangkan bersama komunitas RaketerS. Setiap pemain punya kesempatan yang sama untuk bermain dan berkembang.
                    </Typography>
                    <Button
                        variant="contained"
                        size={isMobile ? 'medium' : 'large'}
                        sx={{
                            background: '#fff',
                            color: '#667eea',
                            fontWeight: 700,
                            px: 4,
                            borderRadius: 2,
                            '&:hover': {
                                background: '#f5f5f5',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 24px rgba(0,0,0,0.2)'
                            }
                        }}
                    >
                        Lihat Event Kami
                    </Button>
                </Container>
            </Box>

            {/* Features Section */}
            <Box sx={{ mb: 8 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                    Mengapa Ikut Friendly Match?
                </Typography>
                <Grid container spacing={3}>
                    {[
                        {
                            icon: <GroupIcon sx={{ fontSize: 40 }} />,
                            title: 'Komunitas Solid',
                            description: 'Bertemu dan bermain dengan pemain-pemain berbakat dari berbagai latar belakang'
                        },
                        {
                            icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
                            title: 'Kualitas Terjamin',
                            description: 'Event terorganisir dengan baik, fasilitas berkualitas, dan pembimbing berpengalaman'
                        },
                        {
                            icon: <StarIcon sx={{ fontSize: 40 }} />,
                            title: 'Pengembangan Skill',
                            description: 'Kesempatan untuk belajar dan berkembang melalui pertandingan yang kompetitif'
                        },
                        {
                            icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
                            title: 'Hadiah Menarik',
                            description: 'Berbagai hadiah untuk tim pemenang dan pemain terbaik di setiap event'
                        }
                    ].map((feature, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    p: 3,
                                    borderRadius: 3,
                                    background: isDark
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'rgba(102, 126, 234, 0.05)',
                                    border: isDark
                                        ? '1px solid rgba(255,255,255,0.1)'
                                        : '1px solid rgba(102, 126, 234, 0.2)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: isDark
                                            ? '0 12px 24px rgba(255,255,255,0.1)'
                                            : '0 12px 24px rgba(102, 126, 234, 0.2)',
                                        background: isDark
                                            ? 'rgba(255,255,255,0.1)'
                                            : 'rgba(102, 126, 234, 0.1)',
                                    }
                                }}
                                elevation={0}
                            >
                                <Box sx={{ color: '#667eea', mb: 2 }}>
                                    {feature.icon}
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
                                    {feature.title}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.8 }}>
                                    {feature.description}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Events Section */}
            <Box sx={{ mb: 8 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
                    Event yang Akan Datang
                </Typography>
                <Grid container spacing={3}>
                    {events.map((event) => (
                        <Grid item xs={12} md={6} lg={4} key={event.id}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    border: isDark
                                        ? '1px solid rgba(255,255,255,0.1)'
                                        : '1px solid rgba(0,0,0,0.1)',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: isDark
                                            ? '0 20px 40px rgba(255,255,255,0.1)'
                                            : '0 20px 40px rgba(0,0,0,0.15)',
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: '#fff',
                                        p: 3,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                        {event.name}
                                    </Typography>
                                    <Chip
                                        label={getStatusLabel(event.status)}
                                        color={getStatusColor(event.status)}
                                        size="small"
                                        sx={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}
                                    />
                                </Box>
                                <CardContent>
                                    <Stack spacing={2}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <EventIcon sx={{ color: '#667eea' }} />
                                            <Typography variant="body2">
                                                <strong>{event.date}</strong>
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TimerIcon sx={{ color: '#667eea' }} />
                                            <Typography variant="body2">
                                                <strong>{event.time}</strong>
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationOnIcon sx={{ color: '#667eea' }} />
                                            <Typography variant="body2">
                                                {event.location}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <GroupIcon sx={{ color: '#667eea' }} />
                                            <Typography variant="body2">
                                                Peserta: <strong>{event.participants} / {event.capacity}</strong>
                                            </Typography>
                                        </Box>
                                        <Box sx={{ width: '100%', height: 8, background: 'rgba(0,0,0,0.1)', borderRadius: 1, overflow: 'hidden' }}>
                                            <Box
                                                sx={{
                                                    width: `${(event.participants / event.capacity) * 100}%`,
                                                    height: '100%',
                                                    background: event.participants === event.capacity ? '#ff6b6b' : '#667eea',
                                                    transition: 'width 0.3s ease'
                                                }}
                                            />
                                        </Box>
                                        <Button
                                            fullWidth
                                            variant={event.status === 'full' ? 'outlined' : 'contained'}
                                            startIcon={<PersonAddIcon />}
                                            onClick={() => handleRegister(event)}
                                            disabled={event.status === 'full'}
                                            sx={{
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                py: 1.2,
                                                mt: 2
                                            }}
                                        >
                                            {event.status === 'full' ? 'Penuh' : 'Daftar Sekarang'}
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    background: isDark
                        ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    py: 6,
                    px: 3,
                    borderRadius: 3,
                    textAlign: 'center',
                    mb: 4
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    Siap untuk Petualangan Badminton Berikutnya?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, mx: 'auto', opacity: 0.95 }}>
                    Jangan lewatkan kesempatan untuk bergabung dengan komunitas badminton terbaik di Indonesia!
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    sx={{
                        background: '#fff',
                        color: '#667eea',
                        fontWeight: 700,
                        px: 4,
                        borderRadius: 2,
                        '&:hover': {
                            background: '#f5f5f5',
                            transform: 'translateY(-2px)',
                        }
                    }}
                >
                    Hubungi Kami Sekarang
                </Button>
            </Box>

            {/* Registration Dialog */}
            <Dialog open={registrationOpen} onClose={() => setRegistrationOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>
                    Pendaftaran Friendly Match
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {selectedEvent && (
                        <>
                            <Typography variant="body2" sx={{ mb: 3, p: 2, background: 'rgba(102, 126, 234, 0.1)', borderRadius: 1 }}>
                                <strong>Event:</strong> {selectedEvent.name}
                            </Typography>
                            <TextField
                                fullWidth
                                label="Nama Lengkap"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Nomor Telepon"
                                name="phone"
                                value={formData.phone}
                                onChange={handleFormChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Level Bermain"
                                name="level"
                                select
                                value={formData.level}
                                onChange={handleFormChange}
                                SelectProps={{
                                    native: true,
                                }}
                                sx={{ mb: 2 }}
                            >
                                <option value="">Pilih Level</option>
                                <option value="pemula">Pemula</option>
                                <option value="menengah">Menengah</option>
                                <option value="mahir">Mahir</option>
                            </TextField>
                            <TextField
                                fullWidth
                                label="Pengalaman Bermain (Tahun)"
                                name="experience"
                                value={formData.experience}
                                onChange={handleFormChange}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setRegistrationOpen(false)}>Batal</Button>
                    <Button
                        onClick={handleSubmitRegistration}
                        variant="contained"
                        disabled={!formData.name || !formData.phone || !formData.level}
                    >
                        Daftar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FriendlyMatch;
